import { useState, useEffect } from "react";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { Box, Button, LinearProgress, Alert, Typography, CircularProgress } from "@mui/material";

const CheckoutForm = ({ onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!stripe) {
      setMessage("Stripe is initializing...");
      return;
    }

    setInitialized(true);

    // Check the payment status to handle cases where the payment might have already completed
    const clientSecret = new URLSearchParams(window.location.search).get(
      "client_secret"
    );

    if (!clientSecret) {
      setMessage("No payment information found");
      return;
    }

    stripe.retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => {
        switch (paymentIntent.status) {
          case "succeeded":
            setMessage("Payment succeeded!");
            onSuccess();
            break;
          case "processing":
            setMessage("Your payment is processing.");
            break;
          case "requires_payment_method":
            setMessage("Please provide your payment details.");
            break;
          default:
            setMessage("Something went wrong.");
            break;
        }
      })
      .catch(error => {
        console.error("Error retrieving payment intent:", error);
        setMessage("Failed to retrieve payment information");
        onError("Failed to retrieve payment information");
      });
  }, [stripe, onSuccess, onError]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      setMessage("The payment system is still initializing. Please wait or refresh the page.");
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Redirect on success
          return_url: `${window.location.origin}/payment/success`,
        },
      });

      // This will only be reached if there's an immediate error when confirming the payment
      if (error) {
        setMessage(error.message);
        onError(error.message);
      }
    } catch (e) {
      console.error("Payment confirmation error:", e);
      setMessage("An unexpected error occurred. Please try again.");
      onError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const paymentElementOptions = {
    layout: "tabs",
  };

  if (!initialized && !stripe) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography>
          Initializing payment system...
        </Typography>
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {message && (
        <Alert 
          severity={message.includes("succeed") ? "success" : message.includes("processing") ? "info" : "error"} 
          sx={{ mb: 3 }}
        >
          {message}
        </Alert>
      )}
      
      {stripe && elements ? (
        <PaymentElement options={paymentElementOptions} />
      ) : (
        <Alert severity="error" sx={{ mb: 3 }}>
          Payment system failed to initialize. Please refresh the page or try again later.
        </Alert>
      )}
      
      <Box sx={{ mt: 3 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={isLoading || !stripe || !elements}
          type="submit"
          size="large"
        >
          {isLoading ? "Processing..." : "Pay Now"}
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress />
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.8rem' }}>
        Your payment is processed securely by Stripe. We do not store your credit card details.
      </Typography>
    </form>
  );
};

export default CheckoutForm; 